import React, { useState, useRef, useEffect } from "react";
import { Typography, DownOutlined, UpOutlined, Tooltip, Button } from "src/assets/imports/antdComponents";
import { IDialogueItem, INotesContextPreview, IReferenceItemProps, StudyType } from "../../models/interfaces";
import { useFeatures } from "src/config/featureManager";
import "./style.css";
import { useTranslation } from "react-i18next";
import { FeatureKeysType } from "src/pages/Studies/models/interfaces";
const { Paragraph } = Typography;

const MAX_ITEMS_TO_SHOW_KC = 2;

const ReferenceItem: React.FC<IReferenceItemProps> = ({ item, onTranscriptClick, activeCitationNo, activeTab, handleFileDownload }) => {
	const { t } = useTranslation();
	const paragraphRef = useRef<HTMLDivElement>(null);
	const citationRef = useRef<HTMLDivElement>(null);

	const { context, context_preview: contextPreview, file_name, entity_name, citation_id, cimg_path_url } = item;
	const isHighlighted = activeCitationNo === citation_id;
	const [expanded, setExpanded] = useState(false);
	const [key, setKey] = useState(0);
	const [copied, setCopied] = useState(false);
	const [isTruncated, setIsTruncated] = useState(false);
	const { isFeatureAvailable } = useFeatures();

	const isMultiLingualSupportEnabled = isFeatureAvailable(FeatureKeysType.MultiLingualSupport);

	useEffect(() => {
		if (activeCitationNo === citation_id && citationRef.current) {
			setTimeout(() => {
				citationRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
			}, 100);
		}
	}, [activeCitationNo, citation_id]);

	const getPlainTextFromDialogue = (dialogueItems: IDialogueItem[]): string => {
		const content = dialogueItems?.map((item) => `[${citation_id}] ${item?.speaker_type}: ${item?.text}`).join("\n") || "";
		const footer = `\nStudy: ${entity_name}\nFile: ${file_name}\n`;
		return content + footer;
	};

	const handleCopy = () => {
		let textToCopy: string;

		if (activeTab === StudyType.Notes || activeTab === StudyType.Web_Boards) {
			const preview = Array.isArray(contextPreview) && contextPreview?.length > 0 ? contextPreview[0] : contextPreview;
			const previewText = typeof preview === "object" && preview?.text !== undefined
				? (preview as INotesContextPreview).text
				: String(preview ?? "");

			textToCopy = `[${citation_id}] ${previewText}\n\nStudy: ${entity_name}\nFile: ${file_name}\n`;
		} else {
			textToCopy = getPlainTextFromDialogue(context);
		}

		navigator.clipboard
			.writeText(textToCopy)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			})
			.catch((err) => {
				console.error("Failed to copy text: ", err);
			});
	};

	const parseDialogue = (parsed: IDialogueItem[], limitItems: boolean = false): React.ReactNode => {
		if (!parsed || parsed.length === 0) return null;

		const itemsToShow = limitItems ? parsed.slice(0, MAX_ITEMS_TO_SHOW_KC) : parsed;

		return itemsToShow?.map((item, index) => {
			const text = item?.text ?? item?.dialog ?? "";
			const speaker = item?.speaker_type ?? "";
			return (
				<p key={`${citation_id}-${index}`} className={`font-normal ${isHighlighted ? "highlight-container" : ""}`}>
					<span className={`dialogue-item ${isHighlighted ? "highlight-block" : ""}`}>
						<strong className="secondary-color">
							[{citation_id}] {speaker}
						</strong>
						: {text}
					</span>
				</p>
			);
		});
	};

	const hasMoreItems = (items: IDialogueItem[]): boolean => {
		return items && items.length > MAX_ITEMS_TO_SHOW_KC;
	};

	const toggleExpanded = () => {
		setExpanded(!expanded);
		setKey((prevKey) => prevKey + 1);
	};

	useEffect(() => {
		const el = paragraphRef.current;
		let observer: ResizeObserver | null = null;

		if (el && !expanded) {
			const checkTruncation = () => {
				setIsTruncated(el.scrollHeight > el.clientHeight);
			};

			checkTruncation();

			observer = new ResizeObserver(checkTruncation);
			observer.observe(el);
		}

		return () => {
			observer?.disconnect();
		};
	}, [expanded, key, item]);


	const isNotesType = activeTab === StudyType.Notes || activeTab === StudyType.Web_Boards;
	const parsedContextPreview = Array.isArray(contextPreview) ? parseDialogue(contextPreview) : null;
	const parsedContext = Array.isArray(context) ? parseDialogue(context, true) : null;

	// New parser for notes-type context_preview
	const parseNotesPreview = (preview: INotesContextPreview): React.ReactNode => {
		if (!preview?.text) return null;

		const { text, text_highlights } = preview;

		if (!text_highlights || text_highlights.length === 0) {
			return (
				<p className={`font-normal ${isHighlighted ? "highlight-container" : ""}`}>
					<span className={`dialogue-item ${isHighlighted ? "highlight-block" : ""}`}>
						{citation_id ? <strong className="secondary-color">[{citation_id}] </strong> : null}
						{text}
					</span>
				</p>
			);
		}

		const textLength = text.length;
		const sortedHighlights = text_highlights
			.map((h) => ({
				start_index: Math.max(0, Math.min(h.start_index, textLength)),
				end_index: Math.max(0, Math.min(h.end_index, textLength)),
			}))
			.filter((h) => h.start_index < h.end_index && h.start_index < textLength)
			.sort((a, b) => a.start_index - b.start_index);


		const segments: React.ReactNode[] = [];
		let lastIndex = 0;

		sortedHighlights.forEach((highlight, idx) => {
			if (highlight.start_index > lastIndex) {
				segments.push(<span key={`pre-${idx}`}>{text.slice(lastIndex, highlight.start_index)}</span>);
			}
			segments.push(
				<span key={`hl-${idx}`} className={`dialogue-item ${isHighlighted ? "highlight-block" : ""}`}>
					{text.slice(highlight.start_index, highlight.end_index)}
				</span>
			);
			lastIndex = highlight.end_index;
		});

		if (lastIndex < text.length) {
			segments.push(<span key="post">{text.slice(lastIndex)}</span>);
		}

		return (
			<p className={`font-normal ${isHighlighted ? "highlight-container" : ""}`}>
				{citation_id ? <strong className="secondary-color">[{citation_id}] </strong> : null}
				{segments}
			</p>
		);
	};

	return (
		<div className="reference-item" id={`reference-item-${citation_id}`} ref={citationRef}>
			{isMultiLingualSupportEnabled && item?.study_language && (
				<div className="flex items-center mb-3">
					<i className="flex items-start material-symbols-outlined text-[1rem] text-[--neutral-grey] mr-1">translate</i>
					<span className="reference-item-languague">{item.study_language.name}</span>
				</div>
			)}
			{activeTab === StudyType.Transcripts ? (
				<>
					<div className="flex justify-between items-start space-x-1">
						<div className="flex flex-col w-full">
							<Paragraph
								key={key}
								className={`ref-summary-para text-base-1 secondary-color cursor-pointer ${expanded ? "" : "preview-mode"}`}
								onClick={isTruncated ? toggleExpanded : () => { }}
								ref={paragraphRef}>
								{expanded ? parsedContext : parsedContextPreview}
							</Paragraph>
							{expanded && hasMoreItems(context) && (
								<div className="flex items-center self-end mr-[-1.75rem] gap-4" onClick={() => onTranscriptClick(item)}>
									<span className="text-[x-small] font-normal text-[--primary-color-2] cursor-pointer flex items-center">{t("More mentions")}</span>
									<i className="material-symbols-outlined cursor-pointer text-[--primary-color] text-2xl leading-none">keyboard_arrow_right</i>
								</div>
							)}
						</div>
						<div className="copy-icon-reference">
							<Tooltip placement="left" title={copied ? t("Copied to clipboard") : t("Copy")}>
								<i onClick={handleCopy} className="material-symbols-outlined text-[--primary-color] text-2xl ask-kural-icon cursor-pointer">
									{copied ? "check" : "copy_all"}
								</i>
							</Tooltip>
						</div>
					</div>
					<div className="transcript-title-wrapper space-x-1">
						<Tooltip title={item?.is_study_access_available ? "" : t(item?.display_message)} placement="top">
							<div
								className={`transcript-name text-base text-[--secondary-color-2] !font-semibold ${item?.is_study_access_available ? "" : "reference-item-not-available"
									}`}
								onClick={() => onTranscriptClick(item)}>
								{`${entity_name}/${file_name}`}
							</div>
						</Tooltip>

						<i className="material-symbols-outlined text-2xl leading-none cursor-pointer text-[--secondary-color]" onClick={() => onTranscriptClick(item)}>
							keyboard_arrow_right
						</i>
					</div>
				</>
			) : activeTab === StudyType.Notes || activeTab === StudyType.Web_Boards ? (
				<>
					<div className="flex justify-between items-start space-x-1">
						<div className="flex flex-col w-full">
							<Paragraph
								key={key}
								className={`ref-summary-para text-base-1 secondary-color cursor-pointer ${expanded ? "" : "preview-mode"}`}
								onClick={isTruncated ? toggleExpanded : () => { }}
								ref={paragraphRef}>
								{(() => {
									const preview = Array.isArray(contextPreview) && contextPreview?.length > 0 ? contextPreview[0] : contextPreview;
									return typeof preview === "object" && preview?.text !== undefined
										? parseNotesPreview(preview as INotesContextPreview)
										: expanded
											? parsedContext
											: parsedContextPreview;
								})()}
							</Paragraph>
						</div>
						<div className="copy-icon-reference">
							<Tooltip placement="left" title={copied ? t("Copied to clipboard") : t("Copy")}>
								<i onClick={handleCopy} className="material-symbols-outlined text-[--primary-color] text-2xl ask-kural-icon cursor-pointer">
									{copied ? "check" : "copy_all"}
								</i>
							</Tooltip>
						</div>
					</div>
					<div className="transcript-title-wrapper space-x-1">
						<Tooltip title={item?.is_study_access_available ? "" : t(item?.display_message || "")} placement="top">
							<div className={`notes-name text-base-1 ${item?.is_study_access_available ? "" : "reference-item-not-available"}`}>{`${entity_name}/${file_name}`}</div>
						</Tooltip>
						{(isTruncated || expanded) && (
							<button onClick={toggleExpanded} className="toggle-icon">
								{expanded ? <UpOutlined className="toggle-icon" /> : <DownOutlined className="toggle-icon" />}
							</button>
						)}
					</div>
				</>
			) : (
				<>
					<div className={`transcript-title-wrapper mb-2 ${isHighlighted ? "highlighted" : ""}`}>
						<div className="flex w-full justify-between">
							<strong className="!no-underline secondary-color mr-2">[{citation_id}] </strong>
							<Tooltip title={item?.is_study_access_available ? "" : t(item?.display_message || "")} placement="top">
								<div
									className={`report-name text-base-1 ${item?.is_study_access_available ? "" : "reference-item-not-available"}`}
									onClick={() => onTranscriptClick(item)}>
									{`${entity_name}/${file_name}`}
								</div>
							</Tooltip>
							<Tooltip title={<div className="tooltip-tag-container">{(Array.isArray(contextPreview) && contextPreview?.length > 0 ? contextPreview[0] : contextPreview as unknown as INotesContextPreview)?.text}</div>} placement="left">
								<i className="material-symbols-outlined secondary-color text-2xl ml-auto mr-1">info</i>
							</Tooltip>
							{activeTab === StudyType.Reports && (
								<Tooltip title={item?.is_study_access_available ? "" : t(item?.display_message || "")} placement="topRight">
									<Button
										disabled={!item?.is_study_access_available}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleFileDownload(item);
										}}
										className="m-0 p-0 h-8"
										type="text"
										icon={<i className="material-symbols-outlined text-2xl ml-auto mr-1">download</i>}
									/>
								</Tooltip>
							)}
						</div>
					</div>
					<img src={String(cimg_path_url)} alt={`${file_name}`} className="thumbnail text-base-1" onClick={() => onTranscriptClick(item)} />
				</>
			)}
		</div>
	);
};

export default ReferenceItem;
